import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  AuditChainVerification,
  RequestAudit,
  RequestParticipant,
} from "models/interfaces";
import { formatAuditDateTime } from "./ViewRequestStatusMeta";
import {
  getAuditDeviceIcon,
  getAuditIntegrityMeta,
  getAuditStatusMeta,
} from "./ViewRequestHelper";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";
import { HistoryGlyph } from "./ViewRequestIcons";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr } from "tour";

const styles = viewSignRequestStyles;
const { audit } = viewSignRequestTokens;

/** Sentinel row standing in for the events hidden by the collapse. */
const ELLIPSIS_ID = "__ellipsis__";

interface AuditTrailCardProps {
  requestAudits: RequestAudit[];
  isLoading: boolean;
  /** Restores the full request audit after a per-signer drill-down. */
  handlerFunction: Function;
  setSelectedParticipant: (participant: RequestParticipant | undefined) => void;
  /** True while a single participant's audit is being shown. */
  showFullAuditBtn: boolean;
  /** Replaces the timeline when the audit feature is gated for this plan. */
  gateBlock?: React.ReactNode;
  /** Shows the integrity check; false when the user lacks audit.view. */
  canVerify?: boolean;
  onVerify?: () => void;
  isVerifying?: boolean;
  verification?: AuditChainVerification;
}

/**
 * Right-rail Audit Trail card: a timeline of the request's activity with an
 * event icon per row, collapsed to its newest and oldest events with an inline
 * "show more" disc plus a footer toggle for the full log.
 */
const AuditTrailCard = ({
  requestAudits,
  isLoading,
  handlerFunction,
  setSelectedParticipant,
  showFullAuditBtn,
  gateBlock,
  canVerify = false,
  onVerify,
  isVerifying = false,
  verification,
}: AuditTrailCardProps) => {
  const theme = useTheme();
  // The compact card is half the desktop rail's width, so the footer takes the
  // reference's shorter label.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const [expanded, setExpanded] = useState(false);

  const total = requestAudits.length;
  const canCollapse = total > audit.collapseThreshold;
  const visibleAudits: RequestAudit[] =
    expanded || !canCollapse
      ? requestAudits
      : [
          ...requestAudits.slice(0, audit.headCount),
          { id: ELLIPSIS_ID } as RequestAudit,
          ...requestAudits.slice(-audit.tailCount),
        ];

  const renderConnector = (isLast: boolean) =>
    !isLast && <Box sx={styles.auditConnector} />;

  const renderSkeletonItem = (index: number) => (
    <Box key={`skeleton-${index}`} sx={styles.auditEvent}>
      <Box sx={styles.auditRail}>
        <Skeleton
          variant="circular"
          width={audit.railSize}
          height={audit.railSize}
        />
        {renderConnector(index === 3)}
      </Box>
      <Box sx={{ ...styles.auditContent, flex: 1 }}>
        <Skeleton variant="text" width="45%" height={16} />
        <Skeleton variant="text" width="80%" height={14} />
        <Skeleton variant="text" width="35%" height={14} />
      </Box>
    </Box>
  );

  const renderEllipsisItem = (isLast: boolean) => (
    <Box key={ELLIPSIS_ID} sx={styles.auditEvent}>
      <Box sx={styles.auditRail}>
        <Tooltip title="Show all" arrow placement="top">
          <IconButton
            disableRipple
            onClick={() => setExpanded(true)}
            aria-label="Show all audit events"
            sx={styles.auditEllipsis}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {renderConnector(isLast)}
      </Box>
      <Box sx={styles.auditContent} />
    </Box>
  );

  const renderTimelineItem = (requestAudit: RequestAudit, index: number) => {
    const isLast = index === visibleAudits.length - 1;

    if (requestAudit.id === ELLIPSIS_ID) {
      return renderEllipsisItem(isLast);
    }

    const { icon, bgColor } = getAuditStatusMeta(
      requestAudit.eventType,
      requestAudit.description,
    );
    const description = requestAudit.reason
      ? `${requestAudit.description} Reason: ${requestAudit.reason}`
      : requestAudit.description;

    return (
      <Box key={requestAudit.id} sx={styles.auditEvent}>
        <Box sx={styles.auditRail}>
          <Box sx={styles.auditRailDisc(bgColor)}>{icon}</Box>
          {renderConnector(isLast)}
        </Box>
        <Box sx={styles.auditContent}>
          <Tooltip title={requestAudit.createdBy?.name ?? ""} arrow describeChild>
            <Typography component="div" sx={styles.auditActor}>
              {requestAudit.createdBy?.name}
            </Typography>
          </Tooltip>
          <Typography component="div" sx={styles.auditText}>
            {description}
          </Typography>
          <Typography component="div" sx={styles.auditWhen}>
            {formatAuditDateTime(requestAudit.createdOn)}
          </Typography>
          {requestAudit.deviceDescription && (
            <Tooltip title={requestAudit.deviceDescription} arrow placement="bottom-start">
              <Box sx={styles.auditDevice}>
                {getAuditDeviceIcon(requestAudit.deviceType, requestAudit.deviceDescription)}
                <Typography component="span" sx={styles.auditDeviceText}>
                  {requestAudit.deviceDescription}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const getFooterButton = () => {
    if (showFullAuditBtn) {
      return (
        <Button
          disableRipple
          sx={styles.auditFooterButton}
          onClick={() => {
            setSelectedParticipant(undefined);
            handlerFunction();
            setExpanded(false);
          }}
        >
          See full audit
        </Button>
      );
    }

    if (!canCollapse) return null;

    return (
      <Button
        disableRipple
        sx={styles.auditFooterButton}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded
          ? isCompact
            ? "Show recent only"
            : "Show recent activity only"
          : isCompact
            ? `View full log (${total})`
            : `View full audit log (${total} events)`}
      </Button>
    );
  };

  /** Tamper-evidence bar: invites a check, then shows the result in its status tone. */
  const getIntegrity = () => {
    if (!canVerify || !onVerify || gateBlock || isLoading || !total) return null;

    const meta = getAuditIntegrityMeta(verification);

    return (
      <Box sx={styles.auditIntegrity(meta.tone)} role="status" aria-live="polite">
        <Tooltip title={meta.tooltip ?? ""} arrow placement="top">
          <Box sx={{ display: "flex" }}>{meta.icon}</Box>
        </Tooltip>
        <Box sx={styles.auditIntegrityText}>
          <Typography component="div" className="integrity-title">
            {meta.title}
          </Typography>
          <Typography component="div" sx={styles.auditIntegrityDetail}>
            {meta.detail}
          </Typography>
        </Box>
        <Button
          disableRipple
          className="integrity-action"
          onClick={onVerify}
          disabled={isVerifying}
          aria-label={`${meta.action} audit trail integrity`}
        >
          {isVerifying ? "Checking…" : meta.action}
        </Button>
      </Box>
    );
  };

  const getBody = () => {
    if (gateBlock) return gateBlock;

    if (isLoading) {
      return (
        <Box sx={styles.auditBody}>{[0, 1, 2, 3].map(renderSkeletonItem)}</Box>
      );
    }

    if (!visibleAudits.length) {
      return (
        /* Read-only: an audit trail fills itself, so it carries no action. */
        <EmptyState
          size="medium"
          icon="activity"
          title="No activity recorded yet"
          body="Every view, signature and decline on this request is logged here as it happens."
        />
      );
    }

    const footer = getFooterButton();

    return (
      <>
        <Box sx={styles.auditBody}>
          <Stack>{visibleAudits.map(renderTimelineItem)}</Stack>
        </Box>
        {footer && <Box sx={styles.auditFooter}>{footer}</Box>}
      </>
    );
  };

  return (
    <Box {...tourAttr("viewRequest.audit")} sx={styles.card}>
      <Box sx={styles.auditHeader}>
        <Box sx={styles.auditIconTile}>
          <HistoryGlyph sx={styles.auditIconGlyph} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={styles.auditTitleRow}>
            <Box component="h2" sx={styles.auditTitle}>
              Audit Trail
            </Box>
            <Box component="span" sx={styles.sectionCount()}>
              {total}
            </Box>
          </Box>
          <Typography component="div" sx={styles.auditSub}>
            Activity history for this request
          </Typography>
        </Box>
      </Box>
      {getBody()}
      {getIntegrity()}
    </Box>
  );
};

export default AuditTrailCard;
